import Reveal from '@/components/Reveal';
import Seo from '@/components/Seo';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

/**
 * Editorial FAQ section.
 * - Accepts an array of { q, a } items.
 * - Renders shadcn Accordion with luxury styling.
 * - Emits FAQPage JSON-LD via <Seo> (good for SEO + GEO).
 *
 * NOTE: this component intentionally does NOT set <title> / <description>;
 * that is owned by each page. The Seo here only injects FAQPage structured
 * data on top of the page-level schema.
 */
export default function Faq({
  eyebrow = 'Common questions',
  heading,
  italicWord,
  items = [],
  tone = 'gold', // 'gold' | 'emerald'
}) {
  const accent =
    tone === 'emerald'
      ? 'text-[hsl(var(--bb-emerald))]'
      : 'text-[hsl(var(--bb-gold))]';
  const eyebrowAccent =
    tone === 'emerald' ? 'bb-eyebrow-emerald' : 'bb-eyebrow';

  const faqJsonLd = items.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      }
    : null;

  return (
    <section className="bb-container py-20 md:py-28" data-testid="faq-section">
      {faqJsonLd && <Seo jsonLd={faqJsonLd} />}
      <div className="grid grid-cols-12 gap-10 md:gap-16">
        <div className="col-span-12 lg:col-span-4">
          <Reveal>
            <div className={eyebrowAccent}>{eyebrow}</div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="bb-display text-4xl md:text-5xl mt-4 text-[hsl(var(--bb-text))]">
              {heading}
              {italicWord ? (
                <>
                  {' '}
                  <span className={`bb-italic ${accent}`}>{italicWord}</span>
                </>
              ) : null}
            </h2>
          </Reveal>
        </div>
        <div className="col-span-12 lg:col-span-8">
          <Accordion type="single" collapsible className="w-full">
            {items.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-[hsl(var(--bb-text))]/12 last:border-b-0"
              >
                <AccordionTrigger className="text-left py-6 md:py-7 text-lg md:text-xl text-[hsl(var(--bb-text))] hover:no-underline data-[state=open]:text-[hsl(var(--bb-gold))]">
                  <span className="bb-display tracking-tight pr-6">{item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="pb-7 text-[hsl(var(--bb-text-muted))] text-base leading-relaxed max-w-3xl">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
